import axios, { AxiosError, AxiosResponseTransformer } from 'axios';
import { Settings } from 'luxon';

import { RAILS_API } from '~/lib/constants';

import {
  maybeRedirectForMaintenanceResponse,
  waitForMaintenanceNavigation,
} from './maintenanceResponse';

export const isAxiosError = <T = unknown, D = unknown>(
  error: unknown,
): error is AxiosError<T, D> =>
  error instanceof Error && !!(error as AxiosError).isAxiosError;

export const is404 = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

function configureClockSkew(serverTime: number): void {
  const now = Number(new Date());

  // Positive clockSkew --> client time is ahead of server time
  // clockSkew is measured in milliseconds
  const clockSkew = now - serverTime;

  Settings.now = () => Date.now() - clockSkew;

  window.clockSkew = clockSkew;
}

interface ConfigureAxios {
  authToken?: string | null;
  idpTest?: string | null;
}

let responseInterceptorConfigured = false;

export const configureAxios = ({
  authToken,
  idpTest,
}: ConfigureAxios): void => {
  axios.defaults.baseURL = RAILS_API;
  axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;

  if (idpTest) {
    axios.defaults.headers.common['x-idp'] = idpTest;
  }

  if (responseInterceptorConfigured) return;

  responseInterceptorConfigured = true;

  // This is the one shared place where Mobius can inspect API responses before
  // feature code turns them into screen updates, snackbars, or dialogs.
  axios.interceptors.response.use(
    // Runs after a successful API call. Some "successful" calls are actually
    // browser-followed redirects to the unavailable page, so check that first.
    function axiosInterceptorResponseSuccess(response) {
      if (maybeRedirectForMaintenanceResponse(response)) {
        // Keep the original caller waiting while the browser leaves Mobius.
        return waitForMaintenanceNavigation();
      }

      const serverTimeHeader = response.headers?.['x-server-time'];

      if (!serverTimeHeader) return response;

      const serverTime = Number(serverTimeHeader);

      configureClockSkew(serverTime);

      return response;
    },
    // Runs after a failed API call. Maintenance redirects can land here too,
    // especially when the browser blocks the redirected response from JavaScript.
    function axiosInterceptorResponseError(error) {
      if (isAxiosError(error) && maybeRedirectForMaintenanceResponse(error)) {
        // This avoids showing an in-app error while the unavailable page opens.
        return waitForMaintenanceNavigation();
      }

      return Promise.reject(error);
    },
  );
};

export const addTransformResponse = (
  reponseTransformer: AxiosResponseTransformer,
): AxiosResponseTransformer[] => {
  if (axios.defaults.transformResponse) {
    return Array.isArray(axios.defaults.transformResponse)
      ? [...axios.defaults.transformResponse, reponseTransformer]
      : [axios.defaults.transformResponse, reponseTransformer];
  }

  return [reponseTransformer];
};
